// Ticket-Redesign (design_handoff_tickets) – reine Darstellungsregeln: Statusfarben, Fortschritt,
// Fälligkeitstexte, Hauptaktion je Status, Breiten-Schwellen. Keine React-/DB-Abhängigkeiten.

export const STATUS_REIHENFOLGE = [
  'neu', 'offen', 'in_bearbeitung', 'warten_auf_rueckmeldung', 'warten_auf_dienstleister', 'eskaliert', 'erledigt', 'geschlossen',
] as const;
export type TicketStatus = (typeof STATUS_REIHENFOLGE)[number];

/** Statusfarbe (Prototyp) – Token-Variablen bzw. die drei Status-Eigenfarben des Handoffs. */
export const STATUS_FARBE: Record<string, string> = {
  neu: '#0a9bd8',
  offen: '#0a9bd8',
  in_bearbeitung: 'var(--warn)',
  warten_auf_rueckmeldung: 'var(--info)',
  warten_auf_dienstleister: '#8a63d2',
  eskaliert: '#a24bc9',
  erledigt: 'var(--ok)',
  geschlossen: 'var(--ink3)',
};

export const PRIO_FARBE: Record<string, string> = {
  kritisch: 'var(--bad)',
  hoch: '#e8730c',
  mittel: 'var(--warn)',
  niedrig: 'var(--ink3)',
};

export const PRIO_REIHENFOLGE = ['kritisch', 'hoch', 'mittel', 'niedrig'] as const;

/** Position im 8-stufigen Fortschrittsbalken (0–7); unbekannt → 0. */
export function fortschrittIndex(status: string): number {
  const i = (STATUS_REIHENFOLGE as readonly string[]).indexOf(status);
  return i < 0 ? 0 : i;
}

const abgeschlossen = (status: string) => status === 'erledigt' || status === 'geschlossen';

/** Ganze Kalendertage von heute bis zum Datum (YYYY-MM-DD, lokal; negativ = vergangen). */
export function tageBis(datum: string, heute: string): number {
  const a = new Date(`${heute.slice(0, 10)}T00:00:00`).getTime();
  const b = new Date(`${datum.slice(0, 10)}T00:00:00`).getTime();
  return Math.round((b - a) / 86400000);
}

function datumDE(datum: string, mitJahr = true): string {
  const [j, m, t] = datum.slice(0, 10).split('-');
  if (!j || !m || !t) return '';
  return mitJahr ? `${t}.${m}.${j}` : `${t}.${m}.`;
}

export type FaelligTon = 'leer' | 'abgeschlossen' | 'ueberfaellig' | 'heute' | 'morgen' | 'normal';

/** Fällig-Spalte der Liste (Prototyp): „—“, „Überfällig · 15.10.“, „Heute“, „Morgen“ oder Datum. */
export function faelligListe(faelligAm: string | null | undefined, status: string, heute: string): { text: string; ton: FaelligTon } {
  if (!faelligAm) return { text: '—', ton: 'leer' };
  if (abgeschlossen(status)) return { text: datumDE(faelligAm), ton: 'abgeschlossen' };
  const d = tageBis(faelligAm, heute);
  if (d < 0) return { text: `Überfällig · ${datumDE(faelligAm, false)}`, ton: 'ueberfaellig' };
  if (d === 0) return { text: 'Heute', ton: 'heute' };
  if (d === 1) return { text: 'Morgen', ton: 'morgen' };
  return { text: datumDE(faelligAm), ton: 'normal' };
}

/** Fällig-Kachel im Widget „Fristen & Termine“: Datum + „heute / morgen / in x Tagen / x Tage überfällig“. */
export function faelligWidget(faelligAm: string | null | undefined, status: string, heute: string): { wert: string | null; unter: string | null; ueberfaellig: boolean } {
  if (!faelligAm) return { wert: null, unter: null, ueberfaellig: false };
  const wert = datumDE(faelligAm);
  if (abgeschlossen(status)) return { wert, unter: null, ueberfaellig: false };
  const d = tageBis(faelligAm, heute);
  if (d < 0) return { wert, unter: `${-d} ${d === -1 ? 'Tag' : 'Tage'} überfällig`, ueberfaellig: true };
  if (d === 0) return { wert, unter: 'heute', ueberfaellig: false };
  if (d === 1) return { wert, unter: 'morgen', ueberfaellig: false };
  return { wert, unter: `in ${d} Tagen`, ueberfaellig: false };
}

export type HauptAktionArt = 'uebernehmen' | 'starten' | 'beauftragen' | 'rueckmeldung' | 'schliessen' | 'oeffnen';

export interface HauptAktion {
  art: HauptAktionArt;
  label: string;
  tip: string;
  /** Zielstatus (beauftragen öffnet den Auftrag-Dialog, schliessen den Abschluss-Dialog) */
  zielStatus: string | null;
}

/** Hauptaktion je Status (Handoff §3). Nicht im Papierkorb und nicht im Bearbeiten-Modus. */
export function hauptAktion(p: { status: string; hatZustaendig: boolean; imPapierkorb: boolean; bearbeiten: boolean }): HauptAktion | null {
  if (p.imPapierkorb || p.bearbeiten) return null;
  switch (p.status) {
    case 'neu':
    case 'offen':
    case 'eskaliert':
      return p.hatZustaendig
        ? { art: 'starten', label: 'Bearbeitung starten', tip: 'Status auf „In Bearbeitung“ setzen', zielStatus: 'in_bearbeitung' }
        : { art: 'uebernehmen', label: 'Übernehmen', tip: 'Dir zuweisen und Bearbeitung starten', zielStatus: 'in_bearbeitung' };
    case 'in_bearbeitung':
      return { art: 'beauftragen', label: 'Handwerker beauftragen', tip: 'Auftrag an Dienstleister vorbereiten', zielStatus: null };
    case 'warten_auf_rueckmeldung':
    case 'warten_auf_dienstleister':
      return { art: 'rueckmeldung', label: 'Rückmeldung erhalten', tip: 'Status zurück auf „In Bearbeitung“', zielStatus: 'in_bearbeitung' };
    case 'erledigt':
      return { art: 'schliessen', label: 'Schließen', tip: 'Ticket endgültig schließen', zielStatus: 'geschlossen' };
    case 'geschlossen':
      return { art: 'oeffnen', label: 'Wieder öffnen', tip: 'Status auf „Offen“ setzen', zielStatus: 'offen' };
    default:
      return null;
  }
}

/** „Erledigt“-Knopf neben der Hauptaktion: nur offene, nicht gelöschte Tickets außerhalb des Bearbeitens. */
export function zeigeErledigt(status: string, imPapierkorb: boolean, bearbeiten: boolean): boolean {
  return !abgeschlossen(status) && !imPapierkorb && !bearbeiten;
}

/** Ab dieser Fensterbreite ist das Detail zweispaltig ohne Seiten-Scroll (Handoff §2: ~1250 px). */
export const ZWEISPALTEN_AB = 1250;
export function istZweispaltig(breite: number, hoehe: number): boolean {
  return breite >= ZWEISPALTEN_AB && hoehe >= 500;
}

/** Werkzeugleiste nur mit Icons unter ~1050 px (Handoff §3). */
export function werkzeugNurIcons(breite: number): boolean {
  return breite < 1050;
}

/** Tabellenspalten der Liste (Handoff §1): Tabelle ab ~1200, Objekt ab 1300, Erstellt ab 1400; darunter Karten. */
export function tabellenSpalten(breite: number, hoehe: number): { tabelle: boolean; objekt: boolean; erstellt: boolean } {
  const tabelle = breite >= 1200 && hoehe >= 500;
  return { tabelle, objekt: tabelle && breite >= 1300, erstellt: tabelle && breite >= 1400 };
}

/** Zeit im Verlauf (Prototyp): „Heute, 07:42“ · „Gestern, 18:10“ · „16.10., 08:20“ (älteres Jahr mit Jahreszahl). */
export function verlaufZeit(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((tag(jetzt) - tag(d)) / 86400000);
  if (diff === 0) return `Heute, ${hm}`;
  if (diff === 1) return `Gestern, ${hm}`;
  const dm = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
  return d.getFullYear() === jetzt.getFullYear() ? `${dm}, ${hm}` : `${dm}${d.getFullYear()}, ${hm}`;
}

/** Composer: Haken „Danach ‚Warte auf Mieter‘“ nur, wenn das Ticket noch nicht dort steht und nicht abgeschlossen ist. */
export function zeigeWarteAufMieter(status: string): boolean {
  return status !== 'warten_auf_rueckmeldung' && status !== 'erledigt' && status !== 'geschlossen';
}

/** KI-Sicherheit als Prozent (0–1 oder 0–100 gespeichert). */
export function kiProzent(conf: number | null | undefined): number | null {
  if (conf == null || Number.isNaN(conf)) return null;
  return Math.round(conf <= 1 ? conf * 100 : conf);
}

/** Laufender Timer als „0:00:38“ / „1:12:05“ (Prototyp). */
export function timerText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Nachweis nur mit mindestens einer abgeschlossenen, abrechenbaren Zeit (Regel aus zeitnachweis.ts). */
export function nachweisMoeglich(zeiten: readonly { abrechenbar: boolean; ende: string | null }[]): boolean {
  return zeiten.some(z => z.abrechenbar && !!z.ende);
}

/** Hinweis, wenn es nichts zu bescheinigen gibt (Prototyp-Wortlaut). */
export const NACHWEIS_LEER = 'Keine abrechenbaren Zeiten – es gibt nichts zu bescheinigen.';
